import { useSyncExternalStore } from 'react'

/**
 * The pending "sketch discarded" undo.
 *
 * Cmd+Z is deliberately not the safety net here: `playwright/tests/editor/undo-void-delete.spec.ts`
 * records a Won't Fix for restoring deleted void blocks, `Y.UndoManager` is per-origin so in a
 * collab document one person's undo can revert another's edit, and on a phone there is no Cmd+Z at
 * all. The Undo button is a fresh transform rather than a history pop, which sidesteps all three.
 *
 * The block that owns the sketch is gone by the time the snackbar renders, so the entry cannot live
 * in that block's state. It also must not go through `useAlert()` / the Redux notifications slice:
 * `Alert.actions` carries a URL for `navigate()`, not a callback, and it is a shared Zod-validated
 * server-facing type. A module-scoped slot read through `useSyncExternalStore` is the smallest thing
 * that works.
 *
 * One slot, not a queue: only one discard is in flight at a time, and stacking undo snackbars would
 * make it ambiguous which sketch "Undo" restores.
 */
export interface ScaffoldUndoEntry {
  /** The element type that was discarded, so only that plugin's snackbar renders it. */
  elementType: string
  message: string
  /** Omitted for terminal confirmations such as "Sketch restored." */
  undo?: () => void
}

let pending: ScaffoldUndoEntry | null = null
const listeners = new Set<() => void>()

const emit = () => listeners.forEach(listener => listener())

export const subscribeScaffoldUndo = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getScaffoldUndo = () => pending

export const showScaffoldUndo = (entry: ScaffoldUndoEntry) => {
  pending = entry
  emit()
}

export const dismissScaffoldUndo = () => {
  if (!pending) {
    return
  }
  pending = null
  emit()
}

/**
 * The pending undo when it belongs to `elementType`, otherwise null — so the image and audio
 * plugins can each mount a snackbar and still only one of them renders per discard.
 */
export const useScaffoldUndo = (elementType: string) => {
  const entry = useSyncExternalStore(subscribeScaffoldUndo, getScaffoldUndo, getScaffoldUndo)
  return entry?.elementType === elementType ? entry : null
}
